import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Typography,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  TextField,
  Modal,
  Button,
  Tab,
  Tabs,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ScreenShareOutlinedIcon from "@mui/icons-material/ScreenShareOutlined";
import { collectBtn, headingStyle, xBtn } from "../../style/LapPageStyle";
import { textfieldText } from "../../style/AppointmentStyle";
import { primaryBtnTeal } from "../../style/PatientDetailsStyle";
import MergedReportPrint from "./MergedReportPrint";
import UseFetchMergedReports from "../../helper/UseFetchMergedReports";
import UseFetchMedicalTestList from "../../helper/UseFetchMedicalTestList";
import useDebounce from "../../hooks/UseDebounce";
import {
  deleteExternalLabDocument,
  deleteExternalLabreportMultiple,
} from "../../connections/deleteApi";
import { postMedicalReport } from "../../connections/postApi";
import { labTestUndo, postMarkStatus } from "../../connections/getApi";
import { showToast } from "../../utils/toastUtil";
import UndoIcon from "@mui/icons-material/Undo";

// ─────────────────────────────────────────────
// STATUS CONFIG
// ─────────────────────────────────────────────

const STATUS_NUM_MAP = {
  2: "sample_collecting",
  4: "done",
  3: "processing",
};

const STATUS_TO_NUM = {
  sample_collecting: 2,
  done: 4,
  processing: 3,
};

const statusConfig = {
  pending: {
    label: "Pending",
    color: "#FFF3CD",
    textColor: "#856404",
    border: "#FFEAA7",
  },
  processing: {
    label: "Processing",
    color: "#D1ECF1",
    textColor: "#0C5460",
    border: "#BEE5EB",
  },
  completed: {
    label: "Completed",
    color: "#D4EDDA",
    textColor: "#155724",
    border: "#C3E6CB",
  },
  done: {
    label: "Done",
    color: "#D4EDDA",
    textColor: "#155724",
    border: "#C3E6CB",
  },
  sample_collecting: {
    label: "Sample Collecting",
    color: "#FFF3CD",
    textColor: "#856404",
    border: "#FFEAA7",
  },
  inprocess: {
    label: "In Process",
    color: "#D1ECF1",
    textColor: "#0C5460",
    border: "#BEE5EB",
  },
};

const getStatusKey = (status) => {
  if (typeof status === "number") return STATUS_NUM_MAP[status] || "pending";
  return status || "pending";
};

const isInternalReport = (report_type) =>
  report_type == null || report_type === "internal";

// ─────────────────────────────────────────────
// CHIPS
// ─────────────────────────────────────────────

const TypeChip = ({ type }) => {
  const isInternal = isInternalReport(type);
  return (
    <Chip
      icon={
        isInternal ? (
          <ScienceOutlinedIcon style={{ fontSize: 13, color: "#0C5460" }} />
        ) : (
          <UploadFileOutlinedIcon style={{ fontSize: 13, color: "#5A1E82" }} />
        )
      }
      label={isInternal ? "Internal" : "External"}
      size="small"
      sx={{
        height: 22,
        fontSize: "11px",
        fontWeight: 700,
        backgroundColor: isInternal ? "#D1ECF1" : "#EDE7F6",
        color: isInternal ? "#0C5460" : "#4A148C",
        border: `1px solid ${isInternal ? "#BEE5EB" : "#CE93D8"}`,
        "& .MuiChip-icon": { ml: "4px" },
      }}
    />
  );
};

const StatusChip = ({ status }) => {
  const key = getStatusKey(status);
  const cfg = statusConfig[key] || statusConfig.pending;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        px: 1,
        py: "2px",
        borderRadius: "20px",
        backgroundColor: cfg.color,
        border: `1px solid ${cfg.border}`,
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          backgroundColor: cfg.textColor,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{ fontSize: "11px", fontWeight: 700, color: cfg.textColor }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const TH = ({ children, sx = {} }) => (
  <TableCell
    sx={{
      fontSize: "12px",
      fontWeight: 700,
      color: "#4A5568",
      py: "8px",
      px: "12px",
      backgroundColor: "#F7FAFC",
      borderBottom: "1px solid #E2E8F0",
      whiteSpace: "nowrap",
      ...sx,
    }}
  >
    {children}
  </TableCell>
);

const TD = ({ children, sx = {} }) => (
  <TableCell
    sx={{
      fontSize: "13px",
      py: "10px",
      px: "12px",
      borderBottom: "1px solid #F0F4F8",
      verticalAlign: "middle",
      ...sx,
    }}
  >
    {children}
  </TableCell>
);

// ─────────────────────────────────────────────
// NORMALIZE: flat tests array → composite groups
// Tests with the same composite_test string are grouped under a composite parent.
// ─────────────────────────────────────────────

function normalizeReports(tests = [], patientId) {
  const compositeMap = {};
  const normalItems = [];

  tests.forEach((r) => {
    const item = { ...r, patient_id: r.patient_id ?? patientId };
    if (r.composite_test) {
      if (!compositeMap[r.composite_test]) {
        compositeMap[r.composite_test] = {
          isComposite: true,
          parent_name: r.composite_test,
          report_type: r.report_type,
          composites: [],
        };
      }
      compositeMap[r.composite_test].composites.push(item);
    } else {
      normalItems.push({ isComposite: false, ...item });
    }
  });

  return [...normalItems, ...Object.values(compositeMap)];
}

// ─────────────────────────────────────────────
// DELETE CONFIRMATION MODAL
// ─────────────────────────────────────────────

const ConfirmDeleteModal = ({ open, onClose, onConfirm, label, extra }) => (
  <Modal
    open={open}
    onClose={onClose}
    sx={{
      position: "absolute",
      top: "25%",
      width: "450px",
      margin: "auto",
      borderRadius: "20px",
    }}
  >
    <Box
      sx={{
        mt: 2,
        bgcolor: "background.paper",
        borderRadius: 3.6,
        boxShadow: 24,
        p: 4.5,
        "&:focus": { outline: "none" },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography textAlign="center" fontWeight={700}>
          Are you sure?
        </Typography>
        <Typography textAlign="center">
          You want to delete <b>{label || "this item"}</b>?
        </Typography>
        {extra && (
          <Typography
            textAlign="center"
            sx={{ fontSize: 12, color: "#667085", mt: 0.5 }}
          >
            {extra}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "25px",
          width: "max-content",
          margin: "auto",
          mt: "40px",
        }}
      >
        <Button
          onClick={onConfirm}
          sx={{
            backgroundColor: "#FF392B",
            color: "white",
            width: "150px",
            borderRadius: "5px",
            fontSize: "12px",
            textTransform: "capitalize",
            "&:hover": { backgroundColor: "red" },
          }}
        >
          Delete
        </Button>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: "white",
            color: "black",
            border: "2px solid #FF392B",
            width: "150px",
            borderRadius: "5px",
            fontSize: "12px",
            textTransform: "capitalize",
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  </Modal>
);

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

const MergedReportsModal = ({ patientData = {}, onClose, patient_id }) => {
  const patientId = patientData?.id || patient_id;

  const todayDate = new Date().toISOString().split("T")[0];

  // Filters — declared before hook so they feed the queryKey
  const [filterType, setFilterType] = useState("all");
  const [filterDateStart, setFilterDateStart] = useState(todayDate);
  const [filterDateEnd, setFilterDateEnd] = useState(todayDate);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMedicalTestIds, setFilterMedicalTestIds] = useState([]);
  const [testSearchInput, setTestSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 50;

  const debouncedTestSearch = useDebounce(testSearchInput, 400);

  const { data: medicalTestLists, isLoading: medicalTestLoading } =
    UseFetchMedicalTestList(1, 20, debouncedTestSearch);

  const testOptions = medicalTestLists?.payload?.medical_tests || [];

  // Reset to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [
    filterType,
    filterDateStart,
    filterDateEnd,
    filterStatus,
    filterMedicalTestIds,
  ]);

  const {
    data: apiData,
    isLoading,
    isFetching,
    refetch,
  } = UseFetchMergedReports({
    patientId,
    page,
    perPage,
    reportType:
      filterStatus !== "all"
        ? "internal"
        : filterType !== "all"
          ? filterType
          : undefined,
    status: filterStatus !== "all" ? STATUS_TO_NUM[filterStatus] : undefined,
    startDate: filterDateStart ? `${filterDateStart} 00:00:00` : undefined,
    endDate: filterDateEnd ? `${filterDateEnd} 23:59:59` : undefined,
    medicalTestIds: filterMedicalTestIds.length
      ? filterMedicalTestIds.map((t) => t.id)
      : undefined,
  });

  const [activeTab, setActiveTab] = useState("tests");
  const [testsUI, setTestsUI] = useState([]); // flat normalized array
  const [docsUI, setDocsUI] = useState([]); // flat docs array

  // keyed by item.id — tracks value typed while item is in "Processing" state
  const [processingInputs, setProcessingInputs] = useState({});

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteModalFile, setDeleteModalFile] = useState(false);
  const [deleteFileTarget, setDeleteFileTarget] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  const originalValueRef = useRef({});
  const initialTabSetRef = useRef(false);

  const [showPreview, setShowPreview] = useState(false);

  const handlePrint = () => {
    setTimeout(() => {
      setShowPreview(true);
    }, 500);
  };

  // ── Parse API response ──
  const [hadTestsOnce, setHadTestsOnce] = useState(false);

  useEffect(() => {
    const mtr = apiData?.payload?.medical_test_reports;
    if (!mtr) return;
    const tests = normalizeReports(mtr.tests || [], patientId);
    setTestsUI(tests);
    setDocsUI(mtr.documents || []);
    if (tests.length > 0) setHadTestsOnce(true);
  }, [apiData, patientId]);

  const hasTests = testsUI.length > 0;
  const hasDocs = docsUI.length > 0;

  useEffect(() => {
    if (initialTabSetRef.current) return;
    if (hadTestsOnce || hasDocs) {
      if (!hadTestsOnce && hasDocs) setActiveTab("docs");
      initialTabSetRef.current = true;
    }
  }, [hadTestsOnce, hasDocs]);

  const INTERNAL_STATUS_OPTIONS = [
    { value: "sample_collecting", label: "Sample Collecting" },
    { value: "done", label: "Done" },
  ];

  // API handles all filtering (type, status, date, medical_test_ids)
  const filteredTests = testsUI;

  const totalCount = apiData?.meta?.total_count ?? 0;
  const totalPage = Math.max(1, Math.ceil(totalCount / perPage));

  const makeKey = (type, id) => `${type}_${id}`;

  // ── UI update helpers (flat array) ──
  const updateTestValueUI = (reportId, newValue) => {
    setTestsUI((prev) =>
      prev.map((r) =>
        !r.isComposite && r.id === reportId ? { ...r, value: newValue } : r,
      ),
    );
  };

  const updateTestGeneratedAtUI = (reportId, newDate) => {
    setTestsUI((prev) =>
      prev.map((r) =>
        !r.isComposite && r.id === reportId
          ? { ...r, generated_at: newDate }
          : r,
      ),
    );
  };

  const updateCompositeValueUI = (parentName, subId, newValue) => {
    setTestsUI((prev) =>
      prev.map((r) => {
        if (!r.isComposite || r.parent_name !== parentName) return r;
        return {
          ...r,
          composites: r.composites.map((c) =>
            c.id === subId ? { ...c, value: newValue } : c,
          ),
        };
      }),
    );
  };

  // ── Edit handler ──
  const handleEditValue = async (item, newValue, keyType = "normal") => {
    if (newValue === undefined || newValue === null) return;
    const key = makeKey(keyType, item.id);
    const prevValue =
      originalValueRef.current[key] !== undefined
        ? originalValueRef.current[key]
        : (item.value ?? "");
    if (String(prevValue) === String(newValue)) {
      delete originalValueRef.current[key];
      return;
    }
    const payload = {
      medical_test_report: {
        patient_id: patientId,
        medical_test_id: item.medical_test_id,
        value: newValue,
      },
      id: item.id,
    };
    try {
      const result = await postMedicalReport(payload, item.id);
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
    } catch {
      showToast("Something went wrong", "ERROR");
    } finally {
      delete originalValueRef.current[key];
    }
  };

  // ── Edit generated_at handler (normal rows only) ──
  const handleEditGeneratedAt = async (item, newDate) => {
    if (!newDate) return;
    const key = `date_${item.id}`;
    const prevDate =
      originalValueRef.current[key] !== undefined
        ? originalValueRef.current[key]
        : (item.generated_at || "").split("T")[0];
    if (prevDate === newDate) {
      delete originalValueRef.current[key];
      return;
    }
    const payload = {
      medical_test_report: {
        patient_id: patientId,
        medical_test_id: item.medical_test_id,
        value: item.value ?? "",
        generated_at: newDate,
      },
      id: item.id,
    };
    try {
      const result = await postMedicalReport(payload, item.id);
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
    } catch {
      showToast("Something went wrong", "ERROR");
    } finally {
      delete originalValueRef.current[key];
    }
  };

  // ── Internal test action handlers ──
  const handleCollectSample = async (item) => {
    const res = await postMarkStatus({
      status: "collection",
      medical_test_id: item.medical_test_id,
      invoice_item_id: item.invoice_item_id,
      patient_id: item.patient_id ?? patientId,
    });
    if (res?.status?.status !== "ERROR") {
      setTestsUI((prev) =>
        prev.map((r) =>
          !r.isComposite && r.id === item.id
            ? { ...r, value: "Sample collecting" }
            : r,
        ),
      );
    } else {
      showToast(res?.status?.message || "Error", "ERROR");
    }
  };

  const handleStartTest = async (item) => {
    if (!item.invoice_item_id) {
      showToast("Under progress — yet to be exposed", "INFO");
      return;
    }
    const res = await postMarkStatus({
      status: "inprocess",
      medical_test_id: item.medical_test_id,
      invoice_item_id: item.invoice_item_id,
      patient_id: item.patient_id ?? patientId,
    });
    if (res?.status?.status !== "ERROR") {
      setTestsUI((prev) =>
        prev.map((r) =>
          !r.isComposite && r.id === item.id
            ? { ...r, value: "Processing" }
            : r,
        ),
      );
    } else {
      showToast(res?.status?.message || "Error", "ERROR");
    }
  };

  const handleUndoTest = async (item) => {
    const res = await labTestUndo(
      item.id,
      item.medical_test_id,
      item.invoice_item_id,
      item.patient_id ?? patientId,
    );
    if (res?.status?.status === "SUCCESS") {
      showToast(res?.status?.message || "Undone", "SUCCESS");
      refetch();
    } else {
      showToast(res?.status?.message || "Undo failed", "ERROR");
    }
  };

  const handleProcessingValueSave = async (item, value) => {
    if (!value) return;
    const payload = {
      medical_test_report: {
        patient_id: patientId,
        medical_test_id: item.medical_test_id,
        value,
      },
      id: item.id,
    };
    try {
      const result = await postMedicalReport(payload, item.id);
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
      setTestsUI((prev) =>
        prev.map((r) =>
          !r.isComposite && r.id === item.id ? { ...r, value } : r,
        ),
      );
      setProcessingInputs((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    } catch {
      showToast("Something went wrong", "ERROR");
    }
  };

  // ── Delete report handlers ──
  const openDeleteModal = (target) => {
    setDeleteTarget(target);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    const ids = deleteTarget?.ids || [];
    if (!ids.length) return;
    try {
      const res = await deleteExternalLabreportMultiple(ids);
      if (res?.status?.status === "SUCCESS") {
        const { type, parentName } = deleteTarget;
        setTestsUI((prev) => {
          if (type === "normal") {
            const id = ids[0];
            return prev.filter(
              (r) => !(r.isComposite === false && r.id === id),
            );
          }
          if (type === "subtest") {
            const subId = ids[0];
            return prev
              .map((r) => {
                if (!r.isComposite || r.parent_name !== parentName) return r;
                return {
                  ...r,
                  composites: r.composites.filter((c) => c.id !== subId),
                };
              })
              .filter((r) => !(r.isComposite && r.composites.length === 0));
          }
          if (type === "composite") {
            return prev.filter(
              (r) => !(r.isComposite && r.parent_name === parentName),
            );
          }
          return prev;
        });
        showToast(res?.status?.message || "Deleted", "SUCCESS");
      } else {
        showToast(res?.status?.message || "Delete failed", "ERROR");
      }
    } catch {
      showToast("Something went wrong", "ERROR");
    } finally {
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  // ── Delete document handlers ──
  const openDeleteFileModal = (target) => {
    setDeleteFileTarget(target);
    setDeleteModalFile(true);
  };

  const handleDeleteDocument = async () => {
    const { id } = deleteFileTarget || {};
    if (!id) return;
    try {
      const res = await deleteExternalLabDocument(id);
      if (res?.status?.status === "SUCCESS") {
        setDocsUI((prev) => prev.filter((d) => d.id !== id));
        showToast(res?.status?.message || "Deleted", "SUCCESS");
      } else {
        showToast(res?.status?.message || "Delete failed", "ERROR");
      }
    } catch {
      showToast("Error deleting document", "ERROR");
    } finally {
      setDeleteModalFile(false);
      setDeleteFileTarget(null);
    }
  };

  const flatPrintData = testsUI.flatMap((item) =>
    item.isComposite
      ? item.composites.map((c) => ({
          ...c,
          patient_id: c.patient_id ?? patientId,
        }))
      : [{ ...item, patient_id: item.patient_id ?? patientId }],
  );

  if (showPreview) {
    return (
      <MergedReportPrint
        patientData={patientData}
        reportData={flatPrintData}
        onClose={() => setShowPreview(false)}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "82vw",
        maxHeight: "88vh",
        display: "flex",
        minHeight: "90vh",
        flexDirection: "column",
      }}
    >
      {/* ── Header ── */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        mt={-0.5}
      >
        <Box />
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <ContentPasteIcon sx={{ width: "28px", color: "neutral.teal" }} />
          <Typography
            variant="heading1"
            fontWeight={600}
            gutterBottom
            sx={{ ...headingStyle }}
          >
            All Reports
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon sx={{ color: "red" }} size="small" />
        </IconButton>
      </Box>

      {/* ── Patient Info ── */}
      <Grid
        container
        sx={{ justifyContent: "space-between", width: "100%", mb: 1 }}
      >
        <Typography variant="body2">
          Name: <b>{patientData?.name || patientData?.full_name || "—"}</b>
        </Typography>
        <Typography variant="body2">
          Patient ID: <b>{patientData?.uid || "—"}</b>
        </Typography>
        <Typography variant="body2">
          Mobile: <b>{patientData?.mobile || "—"}</b>
        </Typography>
        <Typography variant="body2">
          Age: <b>{patientData?.age || "—"}</b>
        </Typography>
        <Typography variant="body2">
          Gender: <b>{patientData?.gender || "—"}</b>
        </Typography>
      </Grid>

      {/* ── Scrollable content area (flex:1 keeps pagination footer pinned) ── */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ── Content ── */}
        <>
          {/* Section tabs */}
          {!isLoading && (hadTestsOnce || hasDocs) && (
            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val)}
              sx={{
                mb: 1.5,
                "& .MuiTabs-indicator": { backgroundColor: "neutral.teal" },
                "& .MuiTab-root": {
                  textTransform: "capitalize",
                  fontWeight: 600,
                  fontSize: "14px",
                },
                "& .Mui-selected": { color: "neutral.teal" },
              }}
            >
              <Tab value="tests" label="Lab Tests" />
              {hasDocs && <Tab value="docs" label="Documents" />}
            </Tabs>
          )}

          {/* ── Lab Tests Tab ── */}
          {activeTab === "tests" && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* ── Filter Bar ── */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 1.5,
                  flexWrap: "wrap",
                  px: 1.5,
                  py: 1,
                  backgroundColor: "#F7FAFC",
                  borderRadius: "12px",
                  border: "1px solid #E2E8F0",
                }}
              >
                {/* Type select */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#4A5568",
                    }}
                  >
                    Type:
                  </Typography>
                  <Select
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value);
                      if (e.target.value === "external") setFilterStatus("all");
                    }}
                    size="small"
                    sx={{ fontSize: "12px", minWidth: 120, height: 32 }}
                  >
                    <MenuItem value="all" sx={{ fontSize: "12px" }}>
                      All
                    </MenuItem>
                    <MenuItem value="internal" sx={{ fontSize: "12px" }}>
                      Internal
                    </MenuItem>
                    <MenuItem value="external" sx={{ fontSize: "12px" }}>
                      External
                    </MenuItem>
                  </Select>
                </Box>

                {/* Date range */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#4A5568",
                    }}
                  >
                    From:
                  </Typography>
                  <TextField
                    type="date"
                    value={filterDateStart}
                    onChange={(e) => setFilterDateStart(e.target.value)}
                    size="small"
                    inputProps={{ max: filterDateEnd || undefined }}
                    sx={{ ...textfieldText, width: 148 }}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#4A5568",
                    }}
                  >
                    To:
                  </Typography>
                  <TextField
                    type="date"
                    value={filterDateEnd}
                    onChange={(e) => setFilterDateEnd(e.target.value)}
                    size="small"
                    inputProps={{ min: filterDateStart || undefined }}
                    sx={{ ...textfieldText, width: 148 }}
                  />
                  {(filterDateStart || filterDateEnd) && (
                    <Button
                      size="small"
                      onClick={() => {
                        setFilterDateStart("");
                        setFilterDateEnd("");
                      }}
                      sx={{
                        fontSize: "11px",
                        textTransform: "none",
                        color: "#718096",
                        minWidth: "auto",
                        p: "2px 6px",
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </Box>

                {/* Test search — multi-select autocomplete, sends IDs to API */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    minWidth: 200,
                  }}
                >
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    options={testOptions}
                    getOptionLabel={(option) => option.name || ""}
                    value={filterMedicalTestIds}
                    onChange={(_, newValue) =>
                      setFilterMedicalTestIds(newValue)
                    }
                    onInputChange={(_, val) => setTestSearchInput(val)}
                    loading={medicalTestLoading}
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    noOptionsText={
                      testSearchInput ? "No tests found" : "Type to search"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          filterMedicalTestIds.length ? "" : "Search test…"
                        }
                        size="small"
                        sx={{ ...textfieldText }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {medicalTestLoading ? (
                                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option.id}
                          label={option.name}
                          {...getTagProps({ index })}
                          size="small"
                          sx={{
                            fontSize: "11px",
                            height: 20,
                            maxWidth: 130,
                          }}
                        />
                      ))
                    }
                  />
                </Box>

                {/* Status filter — hide only for external */}
                {filterType !== "external" && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#4A5568",
                      }}
                    >
                      Status:
                    </Typography>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      size="small"
                      sx={{ fontSize: "12px", minWidth: 150, height: 32 }}
                    >
                      <MenuItem value="all" sx={{ fontSize: "12px" }}>
                        All Status
                      </MenuItem>
                      {INTERNAL_STATUS_OPTIONS.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          sx={{ fontSize: "12px" }}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}
              </Box>

              {/* ── Table ── */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  pr: 0.5,
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#CBD5E0",
                    borderRadius: "4px",
                  },
                }}
              >
                {isLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 6 }}
                  >
                    <CircularProgress
                      size={32}
                      sx={{ color: "neutral.teal" }}
                    />
                  </Box>
                ) : !hasTests ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography sx={{ color: "#94A3B8", fontSize: "14px" }}>
                      No reports found for this patient.
                    </Typography>
                  </Box>
                ) : filteredTests.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography sx={{ color: "#94A3B8", fontSize: "14px" }}>
                      No tests match the selected filters.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer
                    sx={{
                      border: "1px solid #E2E8F0",
                      borderRadius: "14px",
                      overflow: "hidden",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TH sx={{ width: 40 }}>#</TH>
                          <TH>Generated Date</TH>
                          <TH>Test Name</TH>
                          <TH>Lab</TH>
                          <TH>Value</TH>
                          <TH>Type / Status</TH>
                          <TH sx={{ width: 56 }}>Action</TH>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTests.map((item, idx) => {
                          // ── Composite parent + sub-rows ──
                          if (item.isComposite) {
                            return (
                              <React.Fragment
                                key={`comp_${item.parent_name}_${idx}`}
                              >
                                {/* Composite parent header row */}
                                <TableRow sx={{ backgroundColor: "#F0F7F9" }}>
                                  <TD
                                    sx={{
                                      color: "#94A3B8",
                                      fontWeight: 600,
                                      textAlign: "center",
                                    }}
                                  >
                                    {idx + 1}
                                  </TD>
                                  <TD colSpan={4}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "13px",
                                          fontWeight: 700,
                                          color: "#1A202C",
                                        }}
                                      >
                                        {item.parent_name}
                                      </Typography>
                                      <Chip
                                        label="Composite"
                                        size="small"
                                        sx={{
                                          height: 18,
                                          fontSize: "10px",
                                          backgroundColor: "#FEF3C7",
                                          color: "#92400E",
                                          border: "1px solid #FDE68A",
                                        }}
                                      />
                                    </Box>
                                  </TD>
                                  <TD />
                                  <TD sx={{ textAlign: "center" }}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        openDeleteModal({
                                          type: "composite",
                                          ids: item.composites
                                            .map((c) => c.id)
                                            .filter(Boolean),
                                          label: item.parent_name,
                                          parentName: item.parent_name,
                                        })
                                      }
                                    >
                                      <DeleteForeverOutlinedIcon
                                        sx={{ fontSize: 18, color: "red" }}
                                      />
                                    </IconButton>
                                  </TD>
                                </TableRow>

                                {/* Sub-test rows */}
                                {item.composites.map((sub, subIdx) => (
                                  <TableRow
                                    key={sub.id ?? subIdx}
                                    sx={{
                                      backgroundColor:
                                        subIdx % 2 === 0
                                          ? "#FAFBFD"
                                          : "#FFFFFF",
                                      "&:hover": {
                                        backgroundColor: "#EFF6FF",
                                      },
                                      "&:last-child td": {
                                        borderBottom: "none",
                                      },
                                    }}
                                  >
                                    <TD
                                      sx={{
                                        color: "#CBD5E0",
                                        textAlign: "center",
                                        pl: "28px",
                                      }}
                                    >
                                      {subIdx + 1}
                                    </TD>
                                    <TD>
                                      <Typography
                                        sx={{
                                          fontSize: "12px",
                                          color: "#4A5568",
                                        }}
                                      >
                                        {formatDate(sub.generated_at)}
                                      </Typography>
                                    </TD>
                                    <TD>
                                      <Typography
                                        sx={{
                                          fontSize: "13px",
                                          fontWeight: 500,
                                          color: "#2D3748",
                                          pl: 1,
                                        }}
                                      >
                                        ↳ {sub.name}
                                      </Typography>
                                    </TD>
                                    <TD>
                                      <Typography
                                        sx={{
                                          fontSize: "12px",
                                          color: "#718096",
                                        }}
                                      >
                                        {sub.lab || "—"}
                                      </Typography>
                                    </TD>
                                    <TD>
                                      <TextField
                                        placeholder="Enter value"
                                        value={
                                          sub.value === null ||
                                          sub.value === undefined ||
                                          sub.value === "Processing" ||
                                          sub.value === "Sample collecting"
                                            ? ""
                                            : sub.value
                                        }
                                        size="small"
                                        onFocus={() => {
                                          const key = makeKey("comp", sub.id);
                                          if (
                                            originalValueRef.current[key] ===
                                            undefined
                                          ) {
                                            originalValueRef.current[key] =
                                              sub.value ?? "";
                                          }
                                        }}
                                        onChange={(e) =>
                                          updateCompositeValueUI(
                                            item.parent_name,
                                            sub.id,
                                            e.target.value,
                                          )
                                        }
                                        onBlur={(e) =>
                                          handleEditValue(
                                            sub,
                                            e.target.value,
                                            "comp",
                                          )
                                        }
                                        sx={textfieldText}
                                      />
                                    </TD>
                                    <TD>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.5,
                                          alignItems: "flex-start",
                                        }}
                                      >
                                        <TypeChip type={sub.report_type} />
                                        {isInternalReport(sub.report_type) && (
                                          <StatusChip status={sub.status} />
                                        )}
                                      </Box>
                                    </TD>
                                    <TD sx={{ textAlign: "center" }}>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          openDeleteModal({
                                            type: "subtest",
                                            ids: [sub.id],
                                            label: sub.name,
                                            parentName: item.parent_name,
                                          })
                                        }
                                      >
                                        <DeleteForeverOutlinedIcon
                                          sx={{ fontSize: 18, color: "red" }}
                                        />
                                      </IconButton>
                                    </TD>
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            );
                          }

                          // ── Normal report row ──
                          return (
                            <TableRow
                              key={item.id ?? idx}
                              sx={{
                                backgroundColor:
                                  idx % 2 === 0 ? "#FFFFFF" : "#FAFBFD",
                                "&:hover": {
                                  backgroundColor: "#EFF6FF",
                                  transition: "background-color 0.15s",
                                },
                                "&:last-child td": { borderBottom: "none" },
                              }}
                            >
                              <TD
                                sx={{
                                  color: "#94A3B8",
                                  fontWeight: 600,
                                  textAlign: "center",
                                }}
                              >
                                {idx + 1}
                              </TD>
                              <TD>
                                <TextField
                                  type="date"
                                  value={
                                    (item.generated_at || "").split("T")[0]
                                  }
                                  size="small"
                                  onFocus={() => {
                                    const key = `date_${item.id}`;
                                    if (
                                      originalValueRef.current[key] ===
                                      undefined
                                    ) {
                                      originalValueRef.current[key] = (
                                        item.generated_at || ""
                                      ).split("T")[0];
                                    }
                                  }}
                                  onChange={(e) =>
                                    updateTestGeneratedAtUI(
                                      item.id,
                                      e.target.value,
                                    )
                                  }
                                  onBlur={(e) =>
                                    handleEditGeneratedAt(item, e.target.value)
                                  }
                                  sx={{ ...textfieldText, minWidth: 130 }}
                                />
                              </TD>
                              <TD>
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#1A202C",
                                  }}
                                >
                                  {item.name}
                                </Typography>
                              </TD>
                              <TD>
                                <Typography
                                  sx={{ fontSize: "12px", color: "#718096" }}
                                >
                                  {item.lab || "—"}
                                </Typography>
                              </TD>
                              <TD>
                                {isInternalReport(item.report_type) ? (
                                  <>
                                    {(item.value === null ||
                                      item.value === undefined) && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          alignItems: "center",
                                        }}
                                      >
                                        <Button
                                          sx={{
                                            ...collectBtn,
                                            fontSize: "12px",
                                            px: "14px",
                                          }}
                                          onClick={() =>
                                            handleCollectSample(item)
                                          }
                                        >
                                          Collect Sample
                                        </Button>
                                      </Box>
                                    )}
                                    {item.value === "Sample collecting" && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          alignItems: "center",
                                        }}
                                      >
                                        <Button
                                          sx={{
                                            ...collectBtn,
                                            fontSize: "12px",
                                            px: "14px",
                                          }}
                                          onClick={() => handleStartTest(item)}
                                        >
                                          Start
                                        </Button>
                                        <IconButton
                                          onClick={() => handleUndoTest(item)}
                                        >
                                          <UndoIcon
                                            sx={{
                                              ...xBtn,
                                              backgroundColor: "#ffac1c",
                                              "&:hover": {
                                                backgroundColor: "#f5a00c",
                                              },
                                              height: 26,
                                              width: 22,
                                              py: "0px",
                                              px: "2px",
                                            }}
                                          />
                                        </IconButton>
                                      </Box>
                                    )}
                                    {item.value === "Processing" && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 0.5,
                                          alignItems: "center",
                                        }}
                                      >
                                        <TextField
                                          placeholder="Enter value"
                                          size="small"
                                          value={
                                            processingInputs[item.id] ?? ""
                                          }
                                          onChange={(e) =>
                                            setProcessingInputs((prev) => ({
                                              ...prev,
                                              [item.id]: e.target.value,
                                            }))
                                          }
                                          onBlur={() =>
                                            handleProcessingValueSave(
                                              item,
                                              processingInputs[item.id],
                                            )
                                          }
                                          sx={textfieldText}
                                        />
                                        <IconButton
                                          onClick={() => handleUndoTest(item)}
                                        >
                                          <UndoIcon
                                            sx={{
                                              ...xBtn,
                                              backgroundColor: "#ffac1c",
                                              "&:hover": {
                                                backgroundColor: "#f5a00c",
                                              },
                                              py: "0px",
                                              px: "2px",
                                            }}
                                          />
                                        </IconButton>
                                      </Box>
                                    )}
                                    {item.value !== "Processing" &&
                                      item.value != null &&
                                      item.value !== "Sample collecting" && (
                                        <TextField
                                          value={item.value ?? ""}
                                          size="small"
                                          onFocus={() => {
                                            const key = makeKey(
                                              "normal",
                                              item.id,
                                            );
                                            if (
                                              originalValueRef.current[key] ===
                                              undefined
                                            ) {
                                              originalValueRef.current[key] =
                                                item.value ?? "";
                                            }
                                          }}
                                          onChange={(e) =>
                                            updateTestValueUI(
                                              item.id,
                                              e.target.value,
                                            )
                                          }
                                          onBlur={(e) =>
                                            handleEditValue(
                                              item,
                                              e.target.value,
                                              "normal",
                                            )
                                          }
                                          sx={textfieldText}
                                        />
                                      )}
                                  </>
                                ) : (
                                  <TextField
                                    value={item.value ?? ""}
                                    size="small"
                                    onFocus={() => {
                                      const key = makeKey("normal", item.id);
                                      if (
                                        originalValueRef.current[key] ===
                                        undefined
                                      ) {
                                        originalValueRef.current[key] =
                                          item.value ?? "";
                                      }
                                    }}
                                    onChange={(e) =>
                                      updateTestValueUI(item.id, e.target.value)
                                    }
                                    onBlur={(e) =>
                                      handleEditValue(
                                        item,
                                        e.target.value,
                                        "normal",
                                      )
                                    }
                                    sx={textfieldText}
                                  />
                                )}
                              </TD>
                              <TD>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: 0.5,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <TypeChip type={item.report_type} />
                                  {isInternalReport(item.report_type) && (
                                    <StatusChip status={item.status} />
                                  )}
                                </Box>
                              </TD>
                              <TD sx={{ textAlign: "center" }}>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    openDeleteModal({
                                      type: "normal",
                                      ids: [item.id],
                                      label: item.name,
                                    })
                                  }
                                >
                                  <DeleteForeverOutlinedIcon
                                    sx={{ fontSize: 18, color: "red" }}
                                  />
                                </IconButton>
                              </TD>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Box>
          )}

          {/* ── Documents Tab ── */}
          {activeTab === "docs" && hasDocs && !isLoading && (
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                pr: 0.5,
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#CBD5E0",
                  borderRadius: "4px",
                },
              }}
            >
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", pt: 1 }}>
                {docsUI.map((doc, idx) => {
                  const file = doc?.attachment;

                  if (!file) {
                    return (
                      <Box
                        key={doc?.id ?? idx}
                        sx={{
                          width: 200,
                          height: 100,
                          borderRadius: "12px",
                          backgroundColor: "#F7FAFC",
                          border: "1px solid #E2E8F0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography sx={{ fontSize: "12px", color: "#94A3B8" }}>
                          No attachment
                        </Typography>
                      </Box>
                    );
                  }

                  const isPDF = file?.content_type === "application/pdf";
                  const isImage = file?.content_type?.startsWith("image/");

                  return (
                    <Box
                      key={doc?.id ?? idx}
                      sx={{
                        width: 260,
                        height: 130,
                        borderRadius: "14px",
                        backgroundColor: "neutral.blueShadeBg",
                        position: "relative",
                        border: "1px solid #E1E1E1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 2,
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => {
                            setViewerFile(file);
                            setViewerOpen(true);
                          }}
                        >
                          <VisibilityOutlinedIcon
                            sx={{ fontSize: 18, color: "neutral.teal" }}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            openDeleteFileModal({
                              id: doc.id,
                              label: file.filename || "this document",
                            })
                          }
                        >
                          <DeleteForeverOutlinedIcon
                            sx={{ fontSize: 18, color: "red" }}
                          />
                        </IconButton>
                      </Box>

                      <Box
                        sx={{
                          textAlign: "center",
                          flexDirection: "column",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {isPDF ? (
                          <PictureAsPdfOutlinedIcon
                            sx={{ fontSize: 30, color: "neutral.teal" }}
                          />
                        ) : isImage ? (
                          <ImageOutlinedIcon
                            sx={{ fontSize: 30, color: "neutral.teal" }}
                          />
                        ) : (
                          <DescriptionOutlinedIcon
                            sx={{ fontSize: 30, color: "neutral.teal" }}
                          />
                        )}
                        <Typography
                          variant="subTitle116px"
                          sx={{
                            mt: 1,
                            maxWidth: "210px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontFamily: "Lato",
                            color: "neutral.black",
                          }}
                        >
                          {file.filename}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </>
      </Box>
      {/* end scrollable content area */}

      {/* ── Pagination footer — always pinned, never jumps ── */}
      {activeTab === "tests" && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pt: 1.5,
            mt: 1,
            borderTop: "1px solid #E2E8F0",
          }}
        >
          <Typography sx={{ fontSize: "12px", color: "#718096" }}>
            {totalCount > 0
              ? `Showing page ${page} of ${totalPage} · ${totalCount} total`
              : ""}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Button
              size="small"
              disabled={page <= 1 || isLoading}
              onClick={() => setPage((p) => p - 1)}
              startIcon={<ArrowBackIcon sx={{ fontSize: "13px !important" }} />}
              sx={{
                fontSize: "12px",
                textTransform: "capitalize",
                color: page <= 1 ? "grey" : "neutral.teal",
              }}
            >
              Previous
            </Button>
            <Box
              sx={{
                px: 1.5,
                py: 0.3,
                borderRadius: "6px",
                backgroundColor: "#E0F2FE",
                border: "1px solid #7DD3FC",
                minWidth: 28,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{ fontSize: "12px", fontWeight: 700, color: "#0369A1" }}
              >
                {page}
              </Typography>
            </Box>
            <Button
              size="small"
              disabled={page >= totalPage || isLoading}
              onClick={() => setPage((p) => p + 1)}
              endIcon={
                <ArrowForwardIcon sx={{ fontSize: "13px !important" }} />
              }
              sx={{
                fontSize: "12px",
                textTransform: "capitalize",
                color: page >= totalPage ? "grey" : "neutral.teal",
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* ── Preview button ── */}
      {activeTab === "tests" && (
        <Grid
          container
          display={"flex"}
          justifyContent={"space-between"}
          sx={{ mt: 2 }}
        >
          <Grid item />
          <Grid item>
            <Button
              variant="contained"
              disabled={!hasTests}
              sx={primaryBtnTeal}
              onClick={handlePrint}
            >
              <Typography fontSize={"1.1rem"}>Preview</Typography>
            </Button>
          </Grid>
        </Grid>
      )}

      {/* ── Delete Report Modal ── */}
      <ConfirmDeleteModal
        open={deleteModal}
        onClose={() => {
          setDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDelete}
        label={deleteTarget?.label || "this report"}
        extra={
          deleteTarget?.type === "composite"
            ? "* This will delete the whole composite parent report."
            : null
        }
      />

      {/* ── Delete Document Modal ── */}
      <ConfirmDeleteModal
        open={deleteModalFile}
        onClose={() => {
          setDeleteModalFile(false);
          setDeleteFileTarget(null);
        }}
        onConfirm={handleDeleteDocument}
        label={deleteFileTarget?.label || "this document"}
      />

      {/* ── File Viewer Modal ── */}
      <Modal
        open={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setViewerFile(null);
        }}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: "90vw",
            height: "90vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: 24,
            position: "relative",
          }}
        >
          <Box
            sx={{
              height: 54,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>
              {viewerFile?.filename || "Preview"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button
                size="small"
                onClick={() =>
                  viewerFile?.url && window.open(viewerFile.url, "_blank")
                }
              >
                <ScreenShareOutlinedIcon sx={{ color: "neutral.teal" }} />
              </Button>
              <IconButton
                onClick={() => {
                  setViewerOpen(false);
                  setViewerFile(null);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ width: "100%", height: "calc(90vh - 54px)" }}>
            {viewerFile?.content_type === "application/pdf" ? (
              <iframe
                title="pdf-viewer"
                src={viewerFile?.url}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            ) : viewerFile?.content_type?.startsWith("image/") ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#000",
                }}
              >
                <img
                  src={viewerFile?.url}
                  alt={viewerFile?.filename || "image"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography>
                  Preview not supported for this file type.
                </Typography>
                <Button
                  sx={{ mt: 2 }}
                  variant="contained"
                  onClick={() =>
                    viewerFile?.url && window.open(viewerFile.url, "_blank")
                  }
                >
                  Download / Open
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default MergedReportsModal;
